import { useRef, useState } from "react";
import { ArrowLeft, ArrowRight, Check, Loader2, X } from "lucide-react";
import {
  ONBOARDING_STEPS,
  applyOnboardingAction,
  withSelectedPlatforms,
  type OnboardingAction,
  type OnboardingProgress,
  type OnboardingStep,
} from "@lucralink/shared";
import { Button } from "@/components/ui/button";
import { useSaveOnboardingProgress } from "@/lib/hooks/useOnboarding";
import { cn } from "@/lib/utils";
import { StepEmpresa } from "./StepEmpresa";
import { StepCanal } from "./StepCanal";
import { StepPlataformas } from "./StepPlataformas";
import { StepCredenciais } from "./StepCredenciais";
import { StepGrupos } from "./StepGrupos";

export const STEP_COPY: Readonly<Record<OnboardingStep, { title: string; subtitle: string }>> = {
  empresa: { title: "Empresa", subtitle: "Como sua conta se chama" },
  canal: { title: "Canal", subtitle: "Conecte WhatsApp ou Telegram" },
  plataformas: { title: "Plataformas", subtitle: "Lojas em que você é afiliado" },
  credenciais: { title: "Credenciais", subtitle: "Dados de afiliado de cada loja" },
  grupos: { title: "Grupos", subtitle: "De onde vêm e para onde vão as ofertas" },
};

interface SetupWizardProps {
  readonly tenantId: string;
  readonly tenantName: string;
  readonly progress: OnboardingProgress;
}

function Stepper({ progress, onGoto }: { progress: OnboardingProgress; onGoto: (step: OnboardingStep) => void }): JSX.Element {
  const currentIndex = ONBOARDING_STEPS.indexOf(progress.currentStep);
  return (
    <ol className="grid grid-cols-5 gap-1.5" aria-label="Passos da configuração">
      {ONBOARDING_STEPS.map((step, index) => {
        const done = progress.completedSteps.includes(step);
        const current = index === currentIndex;
        const reachable = index <= currentIndex;
        return (
          <li key={step}>
            <button
              type="button"
              disabled={!reachable}
              onClick={() => onGoto(step)}
              aria-current={current ? "step" : undefined}
              className="group w-full space-y-1.5 text-left disabled:cursor-default"
            >
              <span
                className={cn(
                  "block h-1 rounded-full transition-colors",
                  done ? "bg-primary" : current ? "bg-primary/60" : "bg-border",
                )}
              />
              <span className={cn("hidden text-[11px] font-medium sm:block", current ? "text-foreground" : "text-muted-foreground")}>
                {done && <Check size={11} className="mr-1 inline text-primary" aria-hidden="true" />}
                {STEP_COPY[step].title}
              </span>
            </button>
          </li>
        );
      })}
    </ol>
  );
}

interface WizardChromeProps {
  readonly draft: OnboardingProgress;
  readonly pending: boolean;
  readonly onMove: (action: OnboardingAction, target?: OnboardingStep) => void;
}

function WizardHeader({ draft, pending, onMove, onClose }: WizardChromeProps & { onClose: () => void }): JSX.Element {
  const step = draft.currentStep;
  const index = ONBOARDING_STEPS.indexOf(step);
  return (
    <header className="space-y-4">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-xs font-semibold uppercase tracking-widest text-primary">
            Passo {index + 1} de {ONBOARDING_STEPS.length}
          </p>
          <h1 id="setup-wizard-title" className="mt-1 text-2xl font-bold tracking-tight text-foreground">
            {STEP_COPY[step].title}
          </h1>
          <p className="text-sm text-muted-foreground">{STEP_COPY[step].subtitle}</p>
        </div>
        <Button type="button" variant="ghost" size="sm" onClick={onClose} disabled={pending}>
          <X size={14} aria-hidden="true" />
          Configurar depois
        </Button>
      </div>
      <Stepper progress={draft} onGoto={(target) => onMove("goto", target)} />
    </header>
  );
}

function WizardFooter({ draft, pending, onMove }: WizardChromeProps): JSX.Element {
  const index = ONBOARDING_STEPS.indexOf(draft.currentStep);
  const isLast = index === ONBOARDING_STEPS.length - 1;
  return (
    <footer className="flex flex-col-reverse gap-2 sm:flex-row sm:items-center sm:justify-between">
      <Button type="button" variant="ghost" onClick={() => onMove("back")} disabled={index === 0 || pending}>
        <ArrowLeft size={15} aria-hidden="true" />
        Voltar
      </Button>
      <div className="flex flex-col-reverse gap-2 sm:flex-row">
        {draft.currentStep !== "empresa" && (
          <Button type="button" variant="outline" onClick={() => onMove("skip")} disabled={pending}>
            Pular por agora
          </Button>
        )}
        <Button type="button" onClick={() => onMove("complete")} disabled={pending}>
          {pending ? <Loader2 size={15} className="animate-spin" aria-hidden="true" /> : null}
          {isLast ? "Concluir" : "Continuar"}
          {!isLast && <ArrowRight size={15} aria-hidden="true" />}
        </Button>
      </div>
    </footer>
  );
}

interface StepBodyProps {
  readonly tenantId: string;
  readonly tenantName: string;
  readonly draft: OnboardingProgress;
  readonly onDraft: (next: OnboardingProgress) => void;
  readonly submitRef: { current: (() => Promise<boolean>) | null };
}

function StepBody({ tenantId, tenantName, draft, onDraft, submitRef }: StepBodyProps): JSX.Element {
  switch (draft.currentStep) {
    case "empresa":
      return (
        <StepEmpresa
          tenantId={tenantId}
          initialName={tenantName}
          onRegisterSubmit={(submit) => {
            submitRef.current = submit;
          }}
        />
      );
    case "canal":
      return <StepCanal />;
    case "plataformas":
      return (
        <StepPlataformas
          selected={draft.selectedPlatforms}
          onChange={(next) => onDraft(withSelectedPlatforms(draft, next))}
        />
      );
    case "credenciais":
      return <StepCredenciais platforms={draft.selectedPlatforms} />;
    case "grupos":
      return <StepGrupos />;
  }
}

/** Wizard de configuracao inicial em 5 passos. Pular e voltar a vontade; cada
 *  movimento grava o progresso no banco, entao a pessoa retoma de onde parou. */
export function SetupWizard({ tenantId, tenantName, progress }: SetupWizardProps): JSX.Element {
  const save = useSaveOnboardingProgress();
  const [draft, setDraft] = useState<OnboardingProgress>(progress);
  const [error, setError] = useState<string | null>(null);
  const submitRef = useRef<(() => Promise<boolean>) | null>(null);

  const persist = async (next: OnboardingProgress): Promise<void> => {
    setError(null);
    try {
      await save.mutateAsync(next);
      setDraft(next);
    } catch {
      setError("Não foi possível salvar seu progresso. Confira a conexão e tente de novo.");
    }
  };

  const move = async (action: OnboardingAction, target?: OnboardingStep): Promise<void> => {
    const isEmpresaSubmit = action === "complete" && draft.currentStep === "empresa";
    if (isEmpresaSubmit && submitRef.current && !(await submitRef.current())) return;
    await persist(applyOnboardingAction(draft, action, target));
  };

  const chrome = { draft, pending: save.isPending, onMove: (a: OnboardingAction, t?: OnboardingStep) => void move(a, t) };

  return (
    <section className="mx-auto max-w-3xl space-y-6" aria-labelledby="setup-wizard-title">
      <WizardHeader {...chrome} onClose={() => void persist({ ...draft, finished: true })} />
      <div className="rounded-xl border border-border bg-card p-4 sm:p-6">
        <StepBody tenantId={tenantId} tenantName={tenantName} draft={draft} onDraft={setDraft} submitRef={submitRef} />
      </div>
      {error && (
        <p role="alert" className="text-sm text-destructive">
          {error}
        </p>
      )}
      <WizardFooter {...chrome} />
    </section>
  );
}
